<template>
  <tr>
    <th>Host OS Type</th>
    <td>
      <div ref="hvtypePickerRef" class="vm-search-select">
        <input type="hidden" name="hv[hvtype]" class="vm-search-select-value" :value="selectedHvtype">
        <button
          type="button"
          class="form-control vm-search-select-trigger"
          :aria-expanded="hvtypeOpen ? 'true' : 'false'"
          @click.stop="toggleHvtypePicker"
        >
          <span class="vm-search-select-trigger-label">{{ selectedHvtypeLabel }}</span>
          <span class="caret"></span>
        </button>
        <div v-if="hvtypeOpen" class="vm-search-select-panel" @click.stop>
          <input
            v-model="hvtypeFilter"
            type="text"
            class="form-control vm-search-select-input"
            placeholder="Filter host OS types"
          >
          <div class="vm-search-select-options">
            <button
              v-for="option in filteredHvtypeOptions"
              :key="`hvtype-${option.value}`"
              type="button"
              class="vm-search-select-option"
              :class="{ 'is-selected': selectedHvtype === option.value }"
              @click="selectHvtype(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </td>
  </tr>

  <tr>
    <th>Attached Networks</th>
    <td>
      <div ref="networkPickerRef" class="hv-network-picker hv-network-dropdown" data-vue-managed="true">
        <template v-for="networkId in selectedNetworkIds" :key="`network-input-${networkId}`">
          <input type="hidden" name="hv[attached_network_ids][]" :value="networkId">
        </template>

        <button type="button" class="form-control hv-network-dropdown-trigger" @click.stop="toggleNetworkPicker">
          <span class="hv-network-dropdown-label">{{ networkTriggerLabel }}</span>
          <span class="caret"></span>
        </button>

        <div class="hv-network-selected-chips">
          <span v-for="network in selectedNetworks" :key="`network-chip-${network.value}`" class="hv-network-selected-chip">
            {{ network.label }}
            <button type="button" class="hv-network-chip-remove" @click.stop="removeNetwork(network.value)">x</button>
          </span>
        </div>

        <div v-if="networkOpen" class="hv-network-picker-panel" @click.stop>
          <div class="hv-network-picker-toolbar">
            <input
              v-model="networkFilter"
              type="text"
              class="form-control hv-network-filter-input"
              placeholder="Filter attached networks"
            >
            <div class="hv-network-picker-actions">
              <button type="button" class="btn btn-sm btn-outline-primary" @click="selectAllVisibleNetworks">All</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearAllNetworks">Clear</button>
            </div>
          </div>
          <div class="hv-network-picker-list">
            <button
              v-for="option in filteredNetworkOptions"
              :key="`network-option-${option.value}`"
              type="button"
              class="hv-network-option hv-network-option-button"
              :class="{ 'is-selected': selectedNetworkIds.includes(option.value) }"
              @click="toggleNetwork(option.value)"
            >
              <span class="hv-network-option-copy">
                <strong>{{ option.label }}</strong>
              </span>
            </button>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  hvtypeOptions: {
    type: Array,
    default: () => []
  },
  initialHvtype: {
    type: [String, Number],
    default: ""
  },
  networkOptions: {
    type: Array,
    default: () => []
  }
})

const hvtypeOpen = ref(false)
const networkOpen = ref(false)
const hvtypeFilter = ref("")
const networkFilter = ref("")
const hvtypePickerRef = ref(null)
const networkPickerRef = ref(null)
const selectedHvtype = ref(String(props.initialHvtype || ""))
const selectedNetworkIds = ref(
  (props.networkOptions || [])
    .filter((option) => option.selected)
    .map((option) => String(option.value))
)

const normalizedHvtypeOptions = computed(() =>
  (props.hvtypeOptions || []).map((option) => ({
    label: option.label,
    value: String(option.value)
  }))
)

const normalizedNetworkOptions = computed(() =>
  (props.networkOptions || []).map((option) => ({
    label: option.label,
    value: String(option.value)
  }))
)

const filteredHvtypeOptions = computed(() => {
  const keyword = hvtypeFilter.value.trim().toLowerCase()
  if (!keyword) return normalizedHvtypeOptions.value
  return normalizedHvtypeOptions.value.filter((option) => option.label.toLowerCase().includes(keyword))
})

const filteredNetworkOptions = computed(() => {
  const keyword = networkFilter.value.trim().toLowerCase()
  if (!keyword) return normalizedNetworkOptions.value
  return normalizedNetworkOptions.value.filter((option) => option.label.toLowerCase().includes(keyword))
})

const selectedHvtypeLabel = computed(() => {
  return normalizedHvtypeOptions.value.find((option) => option.value === selectedHvtype.value)?.label || "Select a host OS type"
})

const selectedNetworks = computed(() => {
  return normalizedNetworkOptions.value.filter((option) => selectedNetworkIds.value.includes(option.value))
})

const networkTriggerLabel = computed(() => {
  if (!selectedNetworks.value.length) return "Select attached networks"
  if (selectedNetworks.value.length === 1) return selectedNetworks.value[0].label
  return `${selectedNetworks.value.length} networks selected`
})

function toggleHvtypePicker() {
  hvtypeOpen.value = !hvtypeOpen.value
  networkOpen.value = false
  if (!hvtypeOpen.value) hvtypeFilter.value = ""
}

function toggleNetworkPicker() {
  networkOpen.value = !networkOpen.value
  hvtypeOpen.value = false
  if (!networkOpen.value) networkFilter.value = ""
}

function selectHvtype(value) {
  selectedHvtype.value = String(value)
  hvtypeOpen.value = false
  hvtypeFilter.value = ""
}

function toggleNetwork(value) {
  const normalized = String(value)
  if (selectedNetworkIds.value.includes(normalized)) {
    selectedNetworkIds.value = selectedNetworkIds.value.filter((item) => item !== normalized)
  } else {
    selectedNetworkIds.value = [...selectedNetworkIds.value, normalized]
  }
}

function removeNetwork(value) {
  selectedNetworkIds.value = selectedNetworkIds.value.filter((item) => item !== String(value))
}

function selectAllVisibleNetworks() {
  const visibleIds = filteredNetworkOptions.value.map((option) => option.value)
  selectedNetworkIds.value = Array.from(new Set([...selectedNetworkIds.value, ...visibleIds]))
}

function clearAllNetworks() {
  selectedNetworkIds.value = []
}

function closeOnOutsideClick(event) {
  if (hvtypePickerRef.value && !hvtypePickerRef.value.contains(event.target)) {
    hvtypeOpen.value = false
    hvtypeFilter.value = ""
  }

  if (networkPickerRef.value && !networkPickerRef.value.contains(event.target)) {
    networkOpen.value = false
    networkFilter.value = ""
  }
}

onMounted(() => {
  document.addEventListener("click", closeOnOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick)
})
</script>

<style scoped>
.hv-network-option-button {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
}

.hv-network-option-button.is-selected {
  background: rgba(61, 92, 126, 0.08);
}

.hv-network-chip-remove {
  margin-left: 6px;
  border: 0;
  background: transparent;
  color: #496680;
  font-size: 11px;
  line-height: 1;
}
</style>
